import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { Mail } from "lucide-react";
import { isMenuDisabled } from "../../../business/helper/Singleton";
import ScheduledItemMenu from "./ScheduledItemMenu";

// Lowercase: these follow "Repeats every " in the rendered sentence.
const FrequencyLookup = {
  1: "day", // Daily
  2: "week", // Weekly
  3: "two weeks", // BiWeekly
  4: "month", // Monthly
};

const GranularityLookup = {
  1: "Hour",
  2: "DayOfWeek",
  3: "DayOfMonth",
};

const ordinal = (value) => {
  const day = Number(value);
  if (!Number.isFinite(day)) return value;
  if (day % 100 >= 11 && day % 100 <= 13) return `${day}th`;
  return `${day}${["th", "st", "nd", "rd"][day % 10] ?? "th"}`;
};

// A time of day takes "at", a weekday or a date takes "on".
const describeGranularity = (granularityId, granularityValue) => {
  switch (Number(granularityId)) {
    case 2:
      return `on ${granularityValue}`;
    case 3:
      return `on the ${ordinal(granularityValue)}`;
    default:
      return `at ${granularityValue}`;
  }
};

const ScheduledItem = ({
  frequencyId,
  granularityId,
  granularityValue,
  date,
  description,
  readed,
  handleDelete,
  handleRead,
  emailDelivery = null,
  canSendToOwner = false,
  onToggleEmail,
  onManageEmail,
  className = "",
}) => {
  const title = `${FrequencyLookup[frequencyId]} ${describeGranularity(
    granularityId,
    granularityValue
  )}`;
  const [isDisabled, setIsDisabled] = useState(isMenuDisabled.state);

  useEffect(() => {
    const event = () => setIsDisabled(isMenuDisabled.state);
    isMenuDisabled.subscribe(event);

    return () => {
      isMenuDisabled.unsubscribe(event);
    };
  }, []);

  return (
    <div
      className={clsx(
        "space-y-2 transition-colors border-l-4 border-l-blue-100 py-2 px-3 hover:bg-whitesmoke-600 cursor-pointer group",
        className,
        {
          "border-l-whitesmoke-100": readed,
          "!bg-gray-100/10 cursor-not-allowed border-l-gray-100/40": isDisabled,
        }
      )}
      data-tooltip-id="tooltipTasks"
      data-tooltip-content={description}
      data-tooltip-place="left"
      data-tooltip-float
      data-tooltip-delay-show="300"
      onClick={() => {
        if (!isDisabled) handleRead();
      }}
    >
      <div className="flex items-center justify-between text-minimal h-6 text-dimgray-200 text-[12px]">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate">
            Repeats every {title} | {new Date(date).toLocaleDateString("en-US")}
          </span>
          {emailDelivery?.sendToMe && (
            <Mail
              size={13}
              className="shrink-0 text-blue-100"
              aria-label="Sent to your email"
              data-testid="email-on"
            />
          )}
        </span>
        <ScheduledItemMenu
          emailDelivery={emailDelivery}
          canSendToOwner={canSendToOwner}
          disabled={isDisabled}
          onToggleEmail={(next) => onToggleEmail?.(next)}
          onManageEmail={() => onManageEmail?.()}
          onDelete={() => handleDelete()}
        />
      </div>

      <p
        className={clsx("text-dark-blue-100 text-[14px] truncate", {
          "font-bold": !readed,
        })}
      >
        “{description}”
      </p>
    </div>
  );
};

export default ScheduledItem;
